import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { FiltersBar } from '../../components/ui/FiltersBar';
import { HttpRequest } from '../../util/http';
import { IFilters, userData } from '../../util/userData';
import { FeedbackTopicsContainer } from './FeedbackTopicsContainer';
import { QuestionMoodContainer } from './QuestionMoodContainer';
import { QuestionTrendContainer } from './QuestionTrendContainer';

export interface ITrendPoint {
  survey: string,
  abbr: string,
  display: string,
  overall: number,
  surveys: number,
  im: number,
  net: number,
  isHidden: number,
  precision: number,
}

export interface IQuestion {
  qid: string
  val: string
  small_text: string
  label: string
  values: ITrendPoint[]
  avannis: string
  drill_to_drilldown: boolean
}

interface ITrendPhpResponse {
  client: string
  if: string
  showMeasureFilter: boolean
  segmentName: string
  segmentCategory: string
  periodLabel: string
  surveyLabel: string
  avidLabel: string
  q: IQuestion[]
}

interface IMoodPhpResponse {
  values: any[]
  segmentName: string
  segmentCategory: string
  segment: string
  question: string
  periodLabel: string
  surveyLabel: string
  avidLabel: string
  commentType: string
  customerName: string
}

export interface IOverviewCodesPhpResponse {
  sugg: ICommentMood[]
  neut: ICommentMood[]
  comp: ICommentMood[]
  segment: string
  segmentCategory: string
  periodLabel: string
  surveyLabel: string
  avidLabel: string
  customerName: string
  customerType: string
  drill_to_commentdetails: boolean
}

export interface ICommentMood {
  name: string
  mood: string
  surveyCount: number
}

export function SingleSurveyDashboardContainer() {
  const [trendChartTitle, setTrendChartTitle] = useState('Overview');
  const [trendBackendResponse, setTrendBackendResponse] = useState<undefined | ITrendPhpResponse>(undefined);
  const [moodBackendResponse, setMoodBackendResponse] = useState<undefined | IMoodPhpResponse>(undefined);
  const [codeOverviewBackendResponse, setCodeOverviewBackendResponse] = useState<undefined | IOverviewCodesPhpResponse>(undefined);
  const [questions, setQuestions] = useState([]);
  const [customerType, setCustomerType] = useState('Bank');
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [isTrendSectionLoading, setIsTrendSectionLoading] = useState(true);
  const [isMoodSectionLoading, setIsMoodSectionLoading] = useState(true);
  const [isSentimentSectionLoading, setIsSentimentSectionLoading] = useState(true);
  const [filters, setFilters] = useState<undefined | IFilters>(undefined);
  const relevantFilters = ['comment', 'dateRange', 'hier', 'listOfPeriods', 'mood', 'period', 'segment', 'survey', 'commentType', 'metric', 'oequestion']
  const [confirmedFilters, setConfirmedFilters] = useState([]);

  useEffect(() => {
    /**
     * Clean out any question / metric from previous pages
     * They are not relevant to this page.
     */
    const currentFilters = userData.getFilters();
    currentFilters.mood = '';
    currentFilters.comment = '';
    currentFilters.commentType = '';
    currentFilters.metric = '';
    currentFilters.measure = '';
    currentFilters.q = '';
    currentFilters.stat = '';

    HttpRequest.fetch('scripts/resetFilters')
      .then((res: any) => {
        currentFilters.stat = res.stat;
        setConfirmedFilters(['stat']);
        userData.updateFilters(currentFilters);
      })

    userData.filtersListen((() => {
      setFilters((prevState) => {
        const newFilters = userData.getFilters();
        let upcomingFiltersUpdateToBeConfirmedList = [];
        let upcomingFiltersUpdateConfirmedList = [];
        let prevExistingFilters = [];
        let upcomingFilters = [];
        if (prevState) {
          prevExistingFilters = _.reduce(prevState, (result, value, key) => {
            return result.concat(key)
          }, [])
        }
        if (newFilters) {
          upcomingFilters = _.reduce(newFilters, (result, value, key) => {
            return result.concat(key)
          }, [])
        }
        relevantFilters.forEach((filterItem) => {
          if (upcomingFilters.includes(filterItem)) {
            upcomingFiltersUpdateToBeConfirmedList = [...upcomingFiltersUpdateToBeConfirmedList, filterItem];
          }
        })
        upcomingFiltersUpdateToBeConfirmedList.forEach((update) => {
          if (prevExistingFilters.includes(update)) {
            if (prevState[update] !== newFilters[update]) {
              if (update !== 'dateRange') {
                upcomingFiltersUpdateConfirmedList = [...upcomingFiltersUpdateConfirmedList, update]
              } else {
                if (update === 'dateRange' && prevState.period === 'custom_range_selector') {
                  upcomingFiltersUpdateConfirmedList = [...upcomingFiltersUpdateConfirmedList, update]
                }
              }
            }
          } else {
            upcomingFiltersUpdateConfirmedList = [...upcomingFiltersUpdateConfirmedList, update]
          }
        })
        setConfirmedFilters(upcomingFiltersUpdateConfirmedList);
        return newFilters;
      });
    }))

  }, [])

  useEffect(() => {
    if (confirmedFilters.length > 0) {
      const data = filters;
      const method = 'POST';

      setTrendChartTitle('Overview')

      setIsTrendSectionLoading(true);
      HttpRequest.fetch<ITrendPhpResponse>('scripts/overview/trend', { data, method })
        .then((trendResponse) => {
          setTrendBackendResponse(trendResponse)
          setIsTrendSectionLoading(false);
        })

      setIsMoodSectionLoading(true);
      HttpRequest.fetch<IMoodPhpResponse>('scripts/comment_moods', { data, method })
        .then((moodResponse) => {
          setMoodBackendResponse(moodResponse)
          setIsMoodSectionLoading(false);
        })

      setIsSentimentSectionLoading(true)
      HttpRequest.fetch<IOverviewCodesPhpResponse>('scripts/overview_codes', { data, method })
        .then((overviewCodes) => {
          setCodeOverviewBackendResponse(overviewCodes)
          setIsSentimentSectionLoading(false)
          setCustomerType(overviewCodes.customerType)
        })
    }
  }, [confirmedFilters])

  useEffect(() => {
    if (trendBackendResponse) {
      setQuestions(trendBackendResponse.q);
      setTrendChartTitle(trendBackendResponse.surveyLabel);
      setIsTrendSectionLoading(false);
    }
  }, [trendBackendResponse])

  useEffect(() => {
    if (isTrendSectionLoading && isMoodSectionLoading && isSentimentSectionLoading) {
      setIsDashboardLoading(false)
    }
  }, [isTrendSectionLoading, isMoodSectionLoading, isSentimentSectionLoading])

  return (
    <>
      <section className="blacklight">
        <FiltersBar
          hideQuestions
          hideSearch
          hideCommentTypes
          showSegmentsWhenBankwideSelected
          showMonthPicker={
            userData.getFilters().hasOwnProperty('enableDateRangeSelection') &&
            userData.getFilters().enableDateRangeSelection
          }
          dateRangeDataFetchStatusControlledBy={'props'}
          dateRangeDataFetchStatus={isDashboardLoading}
        />
        <Container fluid>
          <header>
            <h5 className="dashboardTitle">
              <span style={{ fontSize: '25px', fontWeight: 300 }}>{`${trendChartTitle} Dashboard`}</span>
              &nbsp;&nbsp;
              {isTrendSectionLoading && <i id="loading" className="fa fa-cog fa-spin" />}
            </h5>
            <div className="overview_legend">
              <img src="images/bank_color_15.png" /> {customerType} Average &nbsp; /
              &nbsp;
              <img src="images/avannis_avg_15.png" /> Avannis Average &nbsp;
            </div>
          </header>
          {
            trendBackendResponse &&
            <Row>
              {
                questions.map((question) => {
                  return <QuestionTrendContainer question={question} avidLabel={trendBackendResponse.avidLabel}
                    surveyLabel={trendBackendResponse.surveyLabel}
                    periodLabel={trendBackendResponse.periodLabel}
                    segmentName={trendBackendResponse.segmentName}
                    segmentCategory={trendBackendResponse.segmentCategory}
                    key={question.qid} />
                })
              }
            </Row>
          }
          <Row>
            <Col sm={12} md={6}>
              {
                moodBackendResponse &&
                <QuestionMoodContainer avidLabel={moodBackendResponse.avidLabel}
                  surveyLabel={moodBackendResponse.surveyLabel}
                  periodLabel={moodBackendResponse.periodLabel}
                  segmentName={moodBackendResponse.segmentName}
                  segmentCategory={moodBackendResponse.segmentCategory}
                  values={moodBackendResponse.values}
                  commentType={moodBackendResponse.commentType}
                  customerName={moodBackendResponse.customerName}
                  question={moodBackendResponse.question} segment={moodBackendResponse.segment} />
              }
            </Col>
            <Col sm={12} md={6}>
              {
                codeOverviewBackendResponse &&
                <FeedbackTopicsContainer overviewCodes={codeOverviewBackendResponse} />
              }
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}
